import { escapeHtml } from "./purchaseOrderPdf";

import { t } from "../lib/i18n";

/**
 * JAN（EAN-13 / 短縮 EAN-8）のバーコードラベルシートを、ブラウザ印刷（PDF保存）向けの
 * 完結した HTML 文書に変換する。発注書 PDF と同じ「純関数で HTML を組み、
 * printHtmlDocument で新規タブに出す」経路に相乗りする（新規依存を足さない）。
 *
 * バーは SVG で自前に描く。JAN の符号化は規格で決まった固定表なので外部ライブラリは要らない。
 * 🔴 JAN として成立しないコード（社内独自コード等）は**バーを描かず**コード文字列だけを出す。
 * 読めないバーを描くと「印刷したのにスキャンできない」という最悪の壊れ方をするため、
 * 描けないことを隠さない。
 */

/** 左半分の奇数パリティ（L コード）。添字が数字。 */
const L_CODES = [
  "0001101",
  "0011001",
  "0010011",
  "0111101",
  "0100011",
  "0110001",
  "0101111",
  "0111011",
  "0110111",
  "0001011",
];

/** 左半分の偶数パリティ（G コード）。 */
const G_CODES = [
  "0100111",
  "0110011",
  "0011011",
  "0100001",
  "0011101",
  "0111001",
  "0000101",
  "0010001",
  "0001001",
  "0010111",
];

/** 右半分（R コード）。L コードのビット反転。 */
const R_CODES = [
  "1110010",
  "1100110",
  "1101100",
  "1000010",
  "1011100",
  "1001110",
  "1010000",
  "1000100",
  "1001000",
  "1110100",
];

/**
 * EAN-13 の先頭桁が決める、左6桁のパリティ並び。
 * 先頭桁自体はバーとして描かれず、この並びだけで表現される。
 */
const PARITY_BY_FIRST_DIGIT = [
  "LLLLLL",
  "LLGLGG",
  "LLGGLG",
  "LLGGGL",
  "LGLLGG",
  "LGGLLG",
  "LGGGLL",
  "LGLGLG",
  "LGLGGL",
  "LGGLGL",
];

const START_GUARD = "101";
const CENTER_GUARD = "01010";
const END_GUARD = "101";

/** クワイエットゾーン（左右の余白）。規格の最小値（左11・右7モジュール）に余裕を持たせる。 */
const QUIET_ZONE_MODULES = 10;

/** バーの高さ（モジュール幅を1とした相対値）。 */
const BAR_HEIGHT_MODULES = 60;

/**
 * EAN-13 / EAN-8 のチェックディジットを、チェックディジットを除いた桁列から計算する。
 * 重みは「最も右の桁が ×3」で左へ交互。EAN-13（12桁）と EAN-8（7桁）で
 * 左からの並びが逆に見えるのはこの規則の帰結であって、別規則ではない。
 */
export function computeEanCheckDigit(payload: string): number {
  const n = payload.length;
  let sum = 0;
  for (let i = 0; i < n; i += 1) {
    const digit = payload.charCodeAt(i) - 48;
    const weight = (n - 1 - i) % 2 === 0 ? 3 : 1;
    sum += digit * weight;
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * そのコードをバーとして描けるか（＝13桁か8桁の数字で、チェックディジットが一致するか）。
 * 桁数と文字種だけを見る実装では、1桁打ち間違えたコードが「描けた」ことになり
 * 読めないラベルが出るので、チェックディジットまで検証する。
 */
export function isPrintableJanCode(code: string | null | undefined): boolean {
  if (typeof code !== "string") return false;
  const trimmed = code.trim();
  if (trimmed.length !== 13 && trimmed.length !== 8) return false;
  if (!/^\d+$/.test(trimmed)) return false;
  const payload = trimmed.slice(0, -1);
  const check = trimmed.charCodeAt(trimmed.length - 1) - 48;
  return computeEanCheckDigit(payload) === check;
}

/** EAN-13（13桁）を95モジュールのビット列へ符号化する。 */
export function encodeEan13(code: string): string {
  const digits = code.trim();
  const parity = PARITY_BY_FIRST_DIGIT[digits.charCodeAt(0) - 48];

  let left = "";
  for (let i = 0; i < 6; i += 1) {
    const digit = digits.charCodeAt(i + 1) - 48;
    left += parity[i] === "L" ? L_CODES[digit] : G_CODES[digit];
  }

  let right = "";
  for (let i = 0; i < 6; i += 1) {
    right += R_CODES[digits.charCodeAt(i + 7) - 48];
  }

  return START_GUARD + left + CENTER_GUARD + right + END_GUARD;
}

/** EAN-8（8桁）を67モジュールのビット列へ符号化する。左4桁は L、右4桁は R 固定。 */
export function encodeEan8(code: string): string {
  const digits = code.trim();

  let left = "";
  for (let i = 0; i < 4; i += 1) {
    left += L_CODES[digits.charCodeAt(i) - 48];
  }

  let right = "";
  for (let i = 0; i < 4; i += 1) {
    right += R_CODES[digits.charCodeAt(i + 4) - 48];
  }

  return START_GUARD + left + CENTER_GUARD + right + END_GUARD;
}

/**
 * ビット列を SVG の黒バーへ変換する。連続する1をひとつの矩形へまとめる
 * （1モジュール1矩形だと24枚のシートで数千要素になり印刷が重くなる）。
 */
function barsToSvg(bits: string): string {
  const rects: string[] = [];
  let run = 0;
  for (let i = 0; i <= bits.length; i += 1) {
    if (bits[i] === "1") {
      run += 1;
      continue;
    }
    if (run > 0) {
      rects.push(
        `<rect x="${QUIET_ZONE_MODULES + i - run}" y="0" width="${run}" height="${BAR_HEIGHT_MODULES}" />`,
      );
      run = 0;
    }
  }
  const width = bits.length + QUIET_ZONE_MODULES * 2;
  return `<svg class="bars" viewBox="0 0 ${width} ${BAR_HEIGHT_MODULES}" preserveAspectRatio="none" shape-rendering="crispEdges" role="img" aria-label={t("バーコード")}><rect x="0" y="0" width="${width}" height="${BAR_HEIGHT_MODULES}" fill="#ffffff" />${rects.join("")}</svg>`;
}

/** ラベル1枚ぶんの元データ。数量ぶんだけ同じラベルを繰り返す。 */
export interface BarcodeLabelItem {
  sku: string | null;
  barcode: string | null | undefined;
  title: string | null;
  /** 印刷する枚数。0以下は「印刷しない」＝スキップ件数にも数えない。 */
  qty: number;
}

export interface BarcodeLabelSheetOptions {
  /** シート見出し（例: 仕入先名）。省略時は既定の表題だけを出す。 */
  title?: string;
}

export interface BarcodeLabelSheetResult {
  html: string;
  /** 実際に出たラベルの枚数（明細ごとの qty の合計）。 */
  labelCount: number;
  /** バーコード未設定で1枚も出せなかった商品数。 */
  skippedNoBarcode: number;
  /** バーを描けずコード文字列だけにした商品数。 */
  textOnlyCount: number;
}

/**
 * その明細からラベルが1枚でも出るか（数量 > 0 かつバーコードが空でない）。
 * バーとして描けるか（`isPrintableJanCode`）は別問題＝JAN として読めなくても
 * コード文字列のラベルは出る。
 */
export function producesLabels(item: BarcodeLabelItem): boolean {
  return item.qty > 0 && (item.barcode ?? "").trim() !== "";
}

/**
 * 実際に出るラベルの枚数を、HTML を組まずに数える。
 * 画面が「押す前に枚数を出す」ために使う＝`buildBarcodeLabelSheet` の
 * `labelCount` と必ず一致する（テストで固定してある）。
 */
export function countBarcodeLabels(items: BarcodeLabelItem[]): number {
  return items.reduce(
    (total, item) => (producesLabels(item) ? total + item.qty : total),
    0,
  );
}

function labelHtml(item: BarcodeLabelItem, printable: boolean): string {
  const code = (item.barcode ?? "").trim();
  const bars = printable
    ? barsToSvg(code.length === 13 ? encodeEan13(code) : encodeEan8(code))
    : `<div class="nobars">${t("バーコード画像なし")}</div>`;

  return `<div class="label">
      <div class="title">${escapeHtml(item.title ?? "—")}</div>
      <div class="sku">${escapeHtml(item.sku ?? "—")}</div>
      ${bars}
      <div class="code">${escapeHtml(code)}</div>
    </div>`;
}

/**
 * 明細からラベルシートの HTML を組む。
 * A4・3列グリッド。バーコード未設定はスキップし、件数を呼び出し側へ返して報告させる
 * （黙って減らすと「押したのに全部出ない」が原因不明になる）。
 */
export function buildBarcodeLabelSheet(
  items: BarcodeLabelItem[],
  options: BarcodeLabelSheetOptions = {},
): BarcodeLabelSheetResult {
  const labels: string[] = [];
  let skippedNoBarcode = 0;
  let textOnlyCount = 0;

  for (const item of items) {
    if (item.qty <= 0) continue;

    const code = (item.barcode ?? "").trim();
    if (code === "") {
      skippedNoBarcode += 1;
      continue;
    }

    const printable = isPrintableJanCode(code);
    if (!printable) textOnlyCount += 1;

    const html = labelHtml(item, printable);
    for (let i = 0; i < item.qty; i += 1) {
      labels.push(html);
    }
  }

  const heading = options.title
    ? `<h1>${t("バーコードラベル")}<span class="sub">${escapeHtml(options.title)}</span></h1>`
    : `<h1>${t("バーコードラベル")}</h1>`;

  const html = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8" />
<title>${t("バーコードラベル")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: "Hiragino Sans", "Yu Gothic", "Noto Sans JP", sans-serif; color: #1a1a1a; margin: 0; padding: 10mm; }
  h1 { font-size: 14px; margin: 0 0 8mm; letter-spacing: 0.08em; }
  h1 .sub { font-size: 12px; font-weight: 400; color: #6b7280; margin-left: 12px; }
  .sheet { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; }
  .label { border: 1px solid #d1d5db; border-radius: 2px; padding: 3mm; height: 30mm; display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; page-break-inside: avoid; break-inside: avoid; }
  .label .title { font-size: 10px; line-height: 1.3; max-height: 2.7em; overflow: hidden; }
  .label .sku { font-size: 9px; color: #6b7280; }
  .label .bars { width: 100%; height: 10mm; display: block; }
  .label .nobars { height: 10mm; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #b45309; border: 1px dashed #d1d5db; }
  .label .code { font-size: 10px; text-align: center; letter-spacing: 0.14em; font-variant-numeric: tabular-nums; }
  @media print { body { padding: 0; } @page { size: A4; margin: 10mm; } h1 { display: none; } }
</style>
</head>
<body>
  ${heading}
  <div class="sheet">${labels.join("")}</div>
</body>
</html>`;

  return { html, labelCount: labels.length, skippedNoBarcode, textOnlyCount };
}
